(ns hai.contacts.card
  "The rolodex card every contacts surface shares: avatar and name, then one
   row per field the contact carries, with Delete in the corner ⋮."
  (:require
   [clojure.string :as str]
   [hai.contacts.avatar :as avatar]
   [hai.entity.kebab :as kebab]
   [hai.ui.components.card :as card]
   [hai.ui.icons :as icons]
   [hai.ui.tokens :as t]
   [lambdaisland.ornament :as o]))

;; ── Card shell ────────────────────────────────────────────────────────────────

(o/defstyled rolodex-card card/card :article
  :relative :flex :flex-col
  {:background-color t/--colour-snow
   :border-radius "12px"
   :gap "8px"
   ;; One height for every card, as the note cards have; the fields scroll
   ;; inside it.
   :height "200px"
   :box-sizing "border-box"
   :border           "1px solid var(--colour-rock)"
   :transition       "box-shadow 0.2s, transform 0.15s"}
  [:&:hover {:box-shadow "0 4px 20px rgba(30,8,93,0.07)"}]
  ;; The whole surface opens the contact, so it carries the cursor — the ⋮
  ;; and the links that mean something else keep their own.
  [:&.clickable {:cursor "pointer"}]
  [:.card-menu {:margin-left "auto"}]
  [:.fields
   {:flex "1 1 auto" :min-height "0"
    :display "flex" :flex-direction "column" :gap "8px"
    :overflow-y "auto"
    :scrollbar-width "thin"
    :scrollbar-color "rgba(29,41,57,0.3) transparent"}
   ["&::-webkit-scrollbar" {:width "6px"}]
   ["&::-webkit-scrollbar-track" {:background-color "transparent"}]
   ["&::-webkit-scrollbar-thumb" {:background-color t/--text-secondary-30
                                  :border-radius "3px"}]])

;; ── Layout primitives ─────────────────────────────────────────────────────────

(o/defstyled identity-row :div
  :flex :items-start {:gap "10px"})

;; The first line centres on the 36px avatar beside it — padded down by half
;; the difference — and any further lines of a long name flow on below.
(o/defstyled name-wrap :div
  :flex-1 :min-w-0
  {:padding-top "8px"
   :font-size "15px" :font-weight "600" :line-height "20px"
   :color t/--text-secondary})

(o/defstyled field-row :div
  :flex :items-center {:gap "6px" :min-height "20px"}
  ;; An inline span would set its svg on the text baseline; a flex box centres
  ;; it on the row like the text beside it.
  [:.field-icon {:display "inline-flex" :align-items "center" :flex "0 0 auto"}]
  [:svg {:width "13px" :height "13px" :flex-shrink "0" :opacity "0.4"}]
  [:a
   [:svg {:opacity "0" :transition "opacity 0.15s"}]
   [:&:hover [:svg {:opacity "1"}]]])

(o/defstyled field-text :span
  {:font-size "12px"
   :color t/--text-secondary
   :opacity "0.65"
   :overflow "hidden"
   :text-overflow "ellipsis"
   :white-space "nowrap"
   :line-height "1.4"})

;; ── Helpers ───────────────────────────────────────────────────────────────────

(defn- github-url [login]
  (str "https://github.com/" login))

(defn- linkedin-href [url]
  (if (str/starts-with? (str url) "http") url (str "https://" url)))

;; ── Sub-components ────────────────────────────────────────────────────────────

(defn- email-rows [emails]
  (into [:<>]
        (for [email (sort emails)]
          ^{:key email}
          [field-row
           [:span.field-icon {:title "Email"} [icons/outlined-email]]
           [field-text [:a {:href (str "mailto:" email)
                            :on-click #(.stopPropagation %)
                            :style {:margin-right "auto" :display "inline-flex" :align-items "center" :gap "4px"}}
                        (str email)
                        [icons/outlined-north_east]]]])))

(defn- domain-rows [domains]
  (into [:<>]
        (for [domain (sort domains)]
          ^{:key domain}
          [field-row
           [:span.field-icon {:title "Mailing domain"} [icons/outlined-public]]
           [field-text domain]])))

(defn- github-section [github-login]
  (when (seq github-login)
    [field-row
     [:span.field-icon {:title "GitHub"} [icons/github {:size 13}]]
     [:a {:href   (github-url github-login)
          :target "_blank"
          :on-click #(.stopPropagation %)
          :style  {:font-size "12px" :color t/--text-secondary
                   :opacity "0.65" :text-decoration "none"
                   :margin-right "auto" :display "inline-flex" :align-items "center" :gap "4px"}}
      github-login
      [icons/outlined-north_east]]]))

(defn- linkedin-section [linkedin-url]
  (when (seq linkedin-url)
    [field-row
     {:title "Open LinkedIn profile"}
     [:span.field-icon [icons/outlined-linkedin]]
     [:a {:href   (linkedin-href linkedin-url)
          :target "_blank"
          :on-click #(.stopPropagation %)
          :style  {:font-size "12px" :color t/--text-secondary
                   :opacity "0.65" :text-decoration "none"
                   :margin-right "auto" :display "inline-flex" :align-items "center" :gap "4px"}}
      (last (str/split linkedin-url #"/"))
      [icons/outlined-north_east]]]))

(defn- members-section [members]
  (when (seq members)
    (let [names (->> members
                     (map #(or (:contact/full-name %) "Unnamed"))
                     (str/join ", "))]
      [field-row
       [:span.field-icon {:title "Members"} [icons/outlined-people]]
       [field-text names]])))

;; ── Public entry point ────────────────────────────────────────────────────────

(defn card
  "`on-body-click` fires anywhere on the card save for what already means
   something else: the ⋮ and the links, which stop the click themselves.
   `on-delete` backs the ⋮ menu, firing once its inline confirm is accepted."
  [{:keys [contact on-body-click on-delete]}]
  (let [{:contact/keys [full-name email members org-mailing-namespace
                        github-login linkedin-url]} contact]
    [rolodex-card
     {:class    (when on-body-click "clickable")
      :on-click on-body-click}
     [card/card-top-row
      ;; The menu and its confirm are portaled but bubble up through React to
      ;; here; stopping them keeps a Delete from also opening the contact.
      [:span.card-menu {:on-click #(.stopPropagation %)}
       [kebab/menu
        {:on-delete      on-delete
         :delete-label   "Delete contact"
         :confirm-prompt "Delete this contact?"
         :aria-label     "Contact actions"}]]]
     [identity-row
      [avatar/avatar contact]
      [name-wrap full-name]]
     [:div.fields
      [email-rows email]
      [domain-rows org-mailing-namespace]
      [github-section github-login]
      [linkedin-section linkedin-url]
      [members-section members]]]))
